// SettingsSection — one titled group of preference rows, pure.
//
// A section is a plain <section> grouping a Heading, an optional description,
// and the rows the consumer places as children. It is NOT a card: .claude/research/design/
// 06 §2 is explicit that related things are grouped by whitespace + a shared
// quiet ground, not a box, and that elevation (a shadow) is reserved for the
// few things that genuinely FLOAT (menu, popover, modal). A settings section
// is co-planar content that sits on the page ground — so it groups by
// whitespace and spends no border and no shadow. (This also keeps dim text on
// the page ground, where the token contrast ratios are guaranteed — an
// elevated surface's lighter dark ground would drop --color-fg-dim below AA.)
//
// The heading is level 2 by default (the panel's own title, under the layout's
// H1) — decoupled from visual size, so a section reads calm without shouting.
//
// It sets `container-type: inline-size` so its rows can respond to the
// section's OWN width (they stack when the section is narrow), independent of
// the viewport.
//
// Pure/props-driven: title, description and children arrive as props; no state,
// no I/O. All text is caller-owned (localised), so no Lingui macro here.

import type { ReactNode } from "react";
import { Heading } from "../../Heading/Heading.js";
import { Text } from "../../Text/Text.js";
import "./SettingsSection.css";

export type SettingsSectionProps = {
    // The section title (localised, caller-owned).
    readonly title: ReactNode;
    // Optional one- or two-line framing under the title (dim ink).
    readonly description?: ReactNode;
    // The rows (SettingsRow) or any content this section groups.
    readonly children: ReactNode;
};

export const SettingsSection = ({
    title,
    description,
    children,
}: SettingsSectionProps) => (
    <section className="bilda-settings-section">
        <div className="bilda-settings-section__head">
            <Heading level={2} size="xl">
                {title}
            </Heading>
            {description != null ? (
                <Text tone="dim" size="md" as="p">
                    {description}
                </Text>
            ) : null}
        </div>
        <div className="bilda-settings-section__body">{children}</div>
    </section>
);
